import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, toastNotifications } from '@uag/react-core';
import { MouseEvent, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { TenantLoginProviderType, UserState, ApiResponseOfBasicUserModel } from 'api/v3/models';
import { useForcePasswordReset, getSearchUsersQueryKey } from 'api/v3/user/user';
import { usePermissions } from 'modules/shared/permissions';
import { RowActionVariant } from 'shared/components/extendedDataTable';

export const ForceResetPasswordAction = (
    user: ApiResponseOfBasicUserModel,
    index: number,
    rowActionVariant: RowActionVariant,
) => {
    const { t } = useTranslation();
    const { mutateAsync: forceResetPassword } = useForcePasswordReset();
    const queryClient = useQueryClient();
    const id = useId();
    const canForceResetPassword = usePermissions('ForcePasswordReset', 'User', user);

    if (user.data.state !== UserState.Activated || user.data.loginProviderType !== TenantLoginProviderType.Local) {
        return null;
    }

    const handleForceResetPassword = async (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();

        toastNotifications.loading({
            id,
            message: t('willBe', {
                type: t('user'),
                name: user.data.displayName,
                action: t('forcedToResetPassword'),
            }),
        });
        try {
            await forceResetPassword({ id: user.data.id });
            await queryClient.invalidateQueries({ queryKey: getSearchUsersQueryKey() });

            toastNotifications.sucess({
                message: t('wasSuccessful', {
                    type: t('user'),
                    name: user.data.displayName,
                    action: t('forcedToResetPassword'),
                }),
            });
        } catch {
            toastNotifications.error({
                message: t('wasNotSuccessful', {
                    type: t('user'),
                    name: user.data.displayName,
                    action: t('forcedToResetPassword'),
                }),
            });
        } finally {
            toastNotifications.hide(id);
        }
    };

    return (
        canForceResetPassword
        && (rowActionVariant === RowActionVariant.DEFAULT ? (
            <Tooltip label={t('forceResetPassword')}>
                <ActionIcon onClick={handleForceResetPassword}>
                    <Icon icon="lock_clock" size="xl" />
                </ActionIcon>
            </Tooltip>
        ) : (
            <Menu.Item leftSection={<Icon icon="lock_clock" size="xl" />} onClick={handleForceResetPassword}>
                {t('forceResetPassword')}
            </Menu.Item>
        ))
    );
};
